import { useMemo, useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import type { PreordersGetActive200Data } from '@pos/api-sdk';
import { useLiffWithMember } from '../../hooks/useLiffWithMember';
import { useTenant } from '../../contexts/TenantContext';
import { getApiBase, getApiHeaders } from '../../api/apiUtils';
import { normalizeImageUrl } from '../../utils/imageUrl';
import {
  usePreorderCampaigns,
  type ActivePreorderCampaign,
} from '../../hooks/queries/usePreorders';
import { useCheckoutConfig } from '../../hooks/useCheckoutConfig';
import { usePreorderCheckout } from '@pos/shared';
import type { PreorderFormState, PreorderCartItem } from '@pos/shared';
type CartItem = PreorderCartItem;
import { logErrorToBackend } from '../../utils/errorLogger';
import {
  calculateTaxAmount,
  formatTwdFromCents,
  formatMoney,
  centsToYuan,
} from '@pos/shared/money';
import type { ConvenienceStoreInfo } from '@pos/shared/schemas/shipments';
import {
  CheckoutInvoicePaymentPage,
  validatePreorderForm,
  validateInvoiceForm,
} from '@pos/shared';
import {
  getTodayTaipeiDateStr,
  subDaysFromDateStr,
  formatPickupDateLabel,
  generatePickupDateOptions,
  PreorderPageLoading,
  PreorderCampaignSelect,
  PreorderEmptyState,
  PreorderHeader,
  PreorderProductList,
  PreorderCartSummary,
} from './modules';

/* 使用 shared 的型別：PreorderFormState、CartItem、PickupOption */

interface UserInfo {
  id: string;
  name: string;
  points: number;
  points_yuan_equivalent: number;
}

type PreorderCampaign = PreordersGetActive200Data;

/**
 * 預購頁面：顯示檔期資訊並提供訂單建立表單（面交預購，商品來源＝預購檔期）
 */
export function PreorderPage() {
  const navigate = useNavigate();
  const defaultPickupOptions = generatePickupDateOptions();
  const [selectedCampaign, setSelectedCampaign] =
    useState<ActivePreorderCampaign | null>(null);

  const [formState, setFormState] = useState<PreorderFormState>({
    pickupDate: defaultPickupOptions[0]?.value || '',
    deliveryMethod: 'store_pickup',
    shippingAddress: null,
    convenienceStoreInfo: null,
    customerName: '',
    customerPhone: '',
    customerNote: '',
    invoiceType: 'personal',
    taxId: '',
    invoiceTitle: '',
    carrierType: 'member',
    carrierNum: '',
    loveCode: '',
    email: '',
  });
  const [cart, setCart] = useState<CartItem[]>([]);
  const [orderSummary, setOrderSummary] = useState<{
    orderNumber: string;
    quantity: number;
    totalTwd: number;
  } | null>(null);
  const [_isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [isSharedDialogOpen, setIsSharedDialogOpen] = useState(false);
  /** 表單步驟：1 發票 → 2 付款（Preorder/Online order 共用 2 步結帳） */
  const [formStep, setFormStep] = useState<1 | 2>(1);
  const [_userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [pointsRedeemAmount, _setPointsRedeemAmount] = useState(0); // 要折抵的金額（元）
  const [showRegisterDialog, setShowRegisterDialog] = useState(false); // 顯示註冊確認對話框
  /** 商品 Drawer（點擊卡片一律開啟，內含 Stepper 與加入購物車） */
  const [productDrawerOpen, setProductDrawerOpen] = useState(false);
  const [paymentProvider, setPaymentProvider] = useState<
    'line_pay' | 'ecpay' | 'cvs_cod'
  >('line_pay'); // 選擇的金流方式
  const [ecpayChoosePaymentSelected, setEcpayChoosePaymentSelected] =
    useState<string>(''); // 綠界單一付款方式（如 Credit、ATM）
  const [selectedProduct, setSelectedProduct] = useState<
    PreorderCampaign['products'][0] | null
  >(null); // Drawer 內顯示的商品
  /** Drawer 內 Stepper 的數量（開啟時同步為購物車現有數量或 1） */
  const [drawerQuantity, setDrawerQuantity] = useState(1);
  /** Drawer 內多規格品時「依維度選擇」的目前值（如 { size: 'l', color: 'red' }），所選規格由此推得 */
  const [selectedByDimension, setSelectedByDimension] = useState<Record<
    string,
    string
  > | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // 結帳提交狀態（防止重複點擊）
  const [_isOpeningCvsMap, setIsOpeningCvsMap] = useState(false); // 綠界電子地圖開起中

  /** 由 selectedByDimension 推得目前選中的規格（多規格品時） */
  const selectedDrawerVariant = useMemo(() => {
    const prod = selectedProduct as {
      variants?: Array<{
        id: string;
        unitPriceTwd: number;
        combination?: Record<string, string>;
      }>;
    };
    const variants = prod?.variants ?? [];
    if (variants.length === 0 || !selectedByDimension) return null;
    const match = variants.find(v =>
      Object.entries(v.combination ?? {}).every(
        ([k, val]) => selectedByDimension[k] === val
      )
    );
    return match
      ? {
          id: match.id,
          unitPriceTwd: match.unitPriceTwd,
          combination: match.combination ?? {},
        }
      : null;
  }, [selectedProduct, selectedByDimension]);

  const { startPayment } = usePreorderCheckout();

  // LIFF 整合
  const { profile, ensureMember } = useLiffWithMember();

  // 租戶識別與 Storage
  const { tenantId, storage, isReady: tenantReady } = useTenant();
  const { config: checkoutConfig } = useCheckoutConfig(tenantId ?? null);
  const invoiceEnabled = checkoutConfig?.ecpayInvoiceEnabled ?? true;
  const _apiHeaders = getApiHeaders(tenantId);

  const {
    campaigns,
    isLoading,
    isError,
    error: campaignError,
  } = usePreorderCampaigns({
    revalidateOnFocus: false,
    dedupingInterval: 5000,
  });

  /** 實際使用的檔期：已選檔期 或 僅一檔時直接使用 */
  const campaign: PreorderCampaign | null | undefined = useMemo(() => {
    if (selectedCampaign) return selectedCampaign as PreorderCampaign;
    if (campaigns?.length === 1) return campaigns[0] as PreorderCampaign;
    return undefined;
  }, [selectedCampaign, campaigns]);

  // 從 TenantStorage 讀取購物車（組件掛載時）
  useEffect(() => {
    if (!tenantReady || !storage) return;

    try {
      const savedCart = storage.getItem<Partial<CartItem>[]>('cart');
      if (savedCart && Array.isArray(savedCart)) {
        console.log('[購物車] 從 TenantStorage 讀取購物車（raw）:', savedCart);
        // 清理並轉型不正確的欄位，避免 UI 顯示 NaN
        const normalized = savedCart.filter(Boolean).map(s => ({
          productId: String(s.productId ?? ''),
          productName: String(s.productName ?? ''),
          productPriceTwd: Number.isFinite(Number(s.productPriceTwd))
            ? Number(s.productPriceTwd)
            : 0,
          productImageUrl:
            s.productImageUrl === null
              ? null
              : String(s.productImageUrl ?? null),
          quantity:
            Number.isFinite(Number(s.quantity)) && Number(s.quantity) > 0
              ? Math.floor(Number(s.quantity))
              : 0,
          remainingQuantity: Number.isFinite(Number(s.remainingQuantity))
            ? Number(s.remainingQuantity)
            : 0,
        }));
        setCart(normalized);
      }
    } catch (error) {
      console.error('[購物車] 讀取購物車失敗:', error);
    }
  }, [tenantReady, storage]);

  // 同步購物車到 TenantStorage（每次購物車變更時）
  useEffect(() => {
    if (!tenantReady || !storage) return;

    try {
      if (cart.length > 0) {
        storage.setItem('cart', cart);
        console.log('[購物車] 已同步購物車到 TenantStorage:', cart);
      } else {
        // 如果購物車為空，清除儲存的購物車
        storage.removeItem('cart');
      }
    } catch (error) {
      console.error('[購物車] 同步購物車失敗:', error);
      logErrorToBackend(error, {
        context: {
          action: 'syncCart',
          tenantId,
        },
      });
    }
  }, [cart, tenantReady, storage, tenantId]);

  /** 面交日期選項：例行檔期用 campaign.pickupDates，僅顯示「預購窗口已開始」的日期；否則用未來 5 天 */
  const pickupDateOptions = useMemo(() => {
    if (!campaign?.pickupDates?.length) {
      return generatePickupDateOptions();
    }
    const todayStr = getTodayTaipeiDateStr();
    const daysBeforeStart = campaign.daysBeforeStart;
    const daysBeforeEnd = campaign.daysBeforeEnd;
    // 有預購窗口設定時：只顯示「今天落在該面交日的預購區間內」的日期
    if (
      typeof daysBeforeStart === 'number' &&
      typeof daysBeforeEnd === 'number'
    ) {
      const filtered = campaign.pickupDates.filter(pickup => {
        const windowStart = subDaysFromDateStr(pickup, daysBeforeStart);
        const windowEnd = subDaysFromDateStr(pickup, daysBeforeEnd);
        return todayStr >= windowStart && todayStr <= windowEnd;
      });
      return filtered.map(value => ({
        value,
        label: formatPickupDateLabel(value),
      }));
    }
    return campaign.pickupDates.map(value => ({
      value,
      label: formatPickupDateLabel(value),
    }));
  }, [
    campaign?.pickupDates,
    campaign?.daysBeforeStart,
    campaign?.daysBeforeEnd,
  ]);

  /** 若目前選的取貨日不在「可見」面交日選項內，改選第一個可見選項（依賴 campaign 與 formState，避免陣列參考造成無限更新） */
  useEffect(() => {
    if (pickupDateOptions.length === 0) return;
    const firstValue = pickupDateOptions[0].value;
    const valid = pickupDateOptions.some(
      opt => opt.value === formState.pickupDate
    );
    if (!valid) {
      setFormState(prev => ({ ...prev, pickupDate: firstValue }));
    }
  }, [
    campaign?.pickupDates,
    campaign?.daysBeforeStart,
    campaign?.daysBeforeEnd,
    formState.pickupDate,
    pickupDateOptions,
  ]);

  /** 顯示用商品列表：例行檔期依所選面交日從 pickupQuotas 帶入剩餘數量，其餘用 campaign.products */
  const displayProducts = useMemo<PreorderCampaign['products']>(() => {
    const products = campaign?.products ?? [];
    const quotas = campaign?.pickupQuotas;
    const pickupDate = formState.pickupDate;
    if (!quotas?.length || !pickupDate) return products;
    const dayQuota = quotas.find(q => q.pickupDate === pickupDate);
    if (!dayQuota) return products;
    return products.map(p => {
      const qu = dayQuota.quotas.find(
        q => String(q.productId) === String(p.productId)
      );
      if (!qu) return p;
      return {
        ...p,
        remainingQuantity: qu.remainingQuantity,
        supplyQuantity: qu.supplyQuantity,
        reservedQuantity: qu.reservedQuantity,
      };
    });
  }, [campaign?.products, campaign?.pickupQuotas, formState.pickupDate]);

  /** 開啟綠界 CVS 電子地圖選擇門市（新視窗） */
  const _openCvsMap = async (
    subType: 'UNIMARTC2C' | 'FAMIC2C',
    brand: '7-ELEVEN' | 'FamilyMart'
  ) => {
    if (!tenantId) {
      toast.error('無法取得租戶資訊，請重新整理後再試');
      return;
    }
    setFormState(prev => ({
      ...prev,
      convenienceStoreInfo: prev.convenienceStoreInfo
        ? {
            ...prev.convenienceStoreInfo,
            brand,
            store_id: '',
            store_name: '',
            store_address: '',
            store_phone: '',
          }
        : { brand, store_id: '', store_name: '', store_address: '' },
    }));
    setIsOpeningCvsMap(true);
    try {
      const base = getApiBase();
      const res = await fetch(`${base}/api/ecpay/cvs-map-form`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: tenantId,
          logistics_sub_type: subType,
          is_collection: 'N',
          redirect_uri: window.location.href,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.message || `請求失敗 (${res.status})`);
      }
      const { formAction, formData } = (await res.json()) as {
        formAction: string;
        formData: Record<string, string>;
      };
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = formAction;
      form.target = '_blank';
      for (const [k, v] of Object.entries(formData)) {
        const i = document.createElement('input');
        i.type = 'hidden';
        i.name = k;
        i.value = v;
        form.appendChild(i);
      }
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : '無法開啟門市地圖，請稍後再試'
      );
    } finally {
      setIsOpeningCvsMap(false);
    }
  };

  // 接綠界電子地圖 callback 的 postMessage（彈窗關閉前發送）
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type !== 'cvs-map-selected' || !e.data?.data) return;
      try {
        const o = new URL(getApiBase()).origin;
        if (e.origin !== o) return;
      } catch {
        return;
      }
      const d = e.data.data as {
        store_id?: string;
        store_name?: string;
        store_address?: string;
        brand?: string;
        store_phone?: string;
      };
      if (!d.store_id || !d.store_name || !d.store_address) return;
      const brand = ['7-ELEVEN', 'FamilyMart', 'HiLife', 'OK_MART'].includes(
        d.brand || ''
      )
        ? (d.brand as ConvenienceStoreInfo['brand'])
        : '7-ELEVEN';
      setFormState(prev => ({
        ...prev,
        convenienceStoreInfo: prev.convenienceStoreInfo
          ? {
              ...prev.convenienceStoreInfo,
              store_id: d.store_id!,
              store_name: d.store_name!,
              store_address: d.store_address!,
              brand,
              store_phone: d.store_phone || undefined,
            }
          : {
              brand,
              store_id: d.store_id!,
              store_name: d.store_name!,
              store_address: d.store_address!,
              store_phone: d.store_phone || undefined,
            },
      }));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // 網址帶入 store_id / store_name / store_address / brand（整頁導回時）
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const sid = p.get('store_id');
    const sname = p.get('store_name');
    const saddr = p.get('store_address');
    const sbrand = p.get('brand');
    const sphone = p.get('store_phone');
    if (!sid || !sname || !saddr) return;
    const brand = ['7-ELEVEN', 'FamilyMart', 'HiLife', 'OK_MART'].includes(
      sbrand || ''
    )
      ? (sbrand as ConvenienceStoreInfo['brand'])
      : '7-ELEVEN';
    setFormState(prev => ({
      ...prev,
      deliveryMethod: 'convenience_store_pickup',
      shippingAddress: prev.shippingAddress || {
        name: '',
        phone: '',
        country: 'Taiwan',
      },
      convenienceStoreInfo: {
        brand,
        store_id: sid,
        store_name: sname,
        store_address: saddr,
        store_phone: sphone || undefined,
      },
    }));
    const u = new URL(window.location.href);
    ['store_id', 'store_name', 'store_address', 'brand', 'store_phone'].forEach(
      k => u.searchParams.delete(k)
    );
    window.history.replaceState({}, '', u.pathname + u.search + u.hash);
  }, []);

  // SWR 會自動處理資料獲取，不需要手動調用

  // 計算實際的錯誤狀態：排除 404 響應（這是正常的業務邏輯）
  const isErrorExcluding404 = useMemo(() => {
    // 如果沒有錯誤，返回 false
    if (!isError || !campaignError) {
      return false;
    }
    // 如果響應狀態是 404，不視為錯誤（SWR 會將 404 視為錯誤，但這是正常的業務邏輯）
    if (
      campaignError &&
      typeof campaignError === 'object' &&
      campaignError !== null &&
      'status' in campaignError
    ) {
      const errorWithStatus = campaignError as { status: number };
      if (errorWithStatus.status === 404) {
        return false;
      }
    }
    // 其他情況視為錯誤
    return true;
  }, [isError, campaignError]);

  // 調試日誌（生產環境也記錄，以便診斷問題）
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('🔍 預購檔期查詢狀態:', {
        isLoading,
        isError,
        hasCampaign: !!campaign,
        error: campaignError
          ? {
              message: (() => {
                const err = campaignError as unknown;
                if (err instanceof Error) {
                  return err.message;
                }
                if (
                  typeof err === 'object' &&
                  err !== null &&
                  'message' in err
                ) {
                  return String((err as { message: unknown }).message);
                }
                return String(err);
              })(),
            }
          : null,
      });
      console.log('📦 Campaign:', campaign);
    }
  }, [campaign, isLoading, isError, campaignError]);

  // 記錄 API 錯誤
  useEffect(() => {
    if (campaignError) {
      // campaignError 可能是 Error 或其他類型的錯誤物件
      const error =
        campaignError instanceof Error
          ? campaignError
          : new Error(campaignError?.message || String(campaignError));

      // 提取詳細錯誤資訊
      let errorDetails: Record<string, unknown> = {
        action: 'fetchCampaign',
        tenantId,
        isError,
        url: window.location.href,
      };

      // 如果是網絡錯誤，添加更多診斷資訊
      if (
        error.message.includes('Load failed') ||
        error.message.includes('fetch') ||
        error.message.includes('network') ||
        error.message.includes('Failed to fetch')
      ) {
        try {
          const apiBase = getApiBase();
          const apiUrl = `${apiBase}/api/preorders/active`;

          errorDetails = {
            ...errorDetails,
            errorType: 'NETWORK_ERROR',
            apiBase,
            apiUrl,
            userAgent: navigator.userAgent,
            online: navigator.onLine,
            timestamp: new Date().toISOString(),
            origin: window.location.origin,
            connectionType:
              (
                navigator as Navigator & {
                  connection?: { effectiveType?: string; downlink?: number };
                }
              ).connection?.effectiveType || 'unknown',
            connectionDownlink:
              (
                navigator as Navigator & {
                  connection?: { effectiveType?: string; downlink?: number };
                }
              ).connection?.downlink || 'unknown',
          };

          // 在開發環境中，嘗試檢查 API 健康狀態（非同步，不阻塞錯誤記錄）
          if (import.meta.env.DEV) {
            fetch(`${apiBase}/health`, {
              method: 'GET',
              signal: AbortSignal.timeout(5000),
            })
              .then(healthResponse => {
                const healthStatus = healthResponse.ok
                  ? 'accessible'
                  : `unhealthy (${healthResponse.status})`;
                // 記錄到控制台（不影響錯誤日誌）
                console.log(
                  '[Network Error Diagnosis] API Health:',
                  healthStatus
                );
              })
              .catch(healthError => {
                console.warn(
                  '[Network Error Diagnosis] API Health Check Failed:',
                  healthError
                );
              });
          }
        } catch (e) {
          errorDetails = {
            ...errorDetails,
            errorType: 'NETWORK_ERROR',
            apiBaseError: e instanceof Error ? e.message : String(e),
          };
        }
      }

      // 如果是 API 錯誤（有 status），添加狀態碼
      if (
        campaignError &&
        typeof campaignError === 'object' &&
        campaignError !== null &&
        'status' in campaignError
      ) {
        const errorWithStatus = campaignError as {
          status: number;
          statusText?: string;
          data?: unknown;
        };
        errorDetails = {
          ...errorDetails,
          errorType: 'API_ERROR',
          status: errorWithStatus.status,
          statusText: errorWithStatus.statusText,
          ...(errorWithStatus.data
            ? { responseData: errorWithStatus.data }
            : {}),
        };
      }

      logErrorToBackend(error, {
        context: errorDetails,
        fields: {
          event: 'fetchCampaign',
          errorCode: (errorDetails.errorType as string) || 'UNKNOWN_ERROR',
          url: window.location.href,
        },
      });
    }
  }, [campaignError, tenantId, isError]);

  // 處理登入即加會員結果（點數、姓名供表單使用）；依賴用基本型別避免 ensureMember 每次新物件參考造成無限更新
  useEffect(() => {
    if (!ensureMember) return;
    setUserInfo({
      id: String(ensureMember.id),
      name: ensureMember.name,
      points: ensureMember.points,
      points_yuan_equivalent: ensureMember.points_yuan_equivalent,
    });
    // 僅依賴基本型別欄位，避免 ensureMember 物件參考變動造成無限更新
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ensureMember?.id,
    ensureMember?.name,
    ensureMember?.points,
    ensureMember?.points_yuan_equivalent,
  ]);

  /**
   * 開啟商品 Drawer（點擊或長按卡片時呼叫）
   * 預設數量為購物車現有數量或 1
   */
  const openProductDrawer = (product: PreorderCampaign['products'][0]) => {
    if (product.remainingQuantity <= 0) {
      toast.error('該商品已售罄');
      return;
    }
    const hasVariants = Boolean(
      (
        product as {
          hasVariants?: boolean;
          variants?: Array<{
            id: string;
            unitPriceTwd: number;
            combination?: Record<string, string>;
          }>;
        }
      ).hasVariants &&
        (
          product as {
            variants?: Array<{
              id: string;
              unitPriceTwd: number;
              combination?: Record<string, string>;
            }>;
          }
        ).variants?.length
    );
    const variants =
      (
        product as {
          variants?: Array<{
            id: string;
            unitPriceTwd: number;
            combination?: Record<string, string>;
          }>;
        }
      ).variants ?? [];
    const firstCombination =
      hasVariants && variants[0] ? (variants[0].combination ?? {}) : null;
    setSelectedByDimension(firstCombination);
    const firstVariant =
      hasVariants && variants[0]
        ? {
            id: variants[0].id,
            unitPriceTwd: variants[0].unitPriceTwd,
            combination: variants[0].combination ?? {},
          }
        : null;
    const existing = cart.find(
      item =>
        item.productId === product.productId &&
        (item.productVariantId ?? '') === (firstVariant?.id ?? '')
    );
    setSelectedProduct(product);
    setDrawerQuantity(existing ? existing.quantity : 1);
    setProductDrawerOpen(true);
  };

  /** Drawer 內 Stepper 已達上限／下限（在組件內計算，避免 JSX 內使用 >= / <= 造成解析問題） */
  const drawerStepperAtMax =
    selectedProduct != null &&
    drawerQuantity >= selectedProduct.remainingQuantity;
  const drawerStepperAtMin = drawerQuantity <= 1;

  const handleDrawerStepperMinus = () =>
    setDrawerQuantity(q => Math.max(1, q - 1));
  const handleDrawerStepperPlus = () => {
    if (selectedProduct)
      setDrawerQuantity(q =>
        Math.min(selectedProduct.remainingQuantity, q + 1)
      );
  };
  const handleDrawerAddToCart = () => {
    if (selectedProduct) {
      const prod = selectedProduct as {
        hasVariants?: boolean;
        variants?: Array<{ id: string }>;
      };
      if (prod.hasVariants && !selectedDrawerVariant) {
        toast.error('請選擇規格');
        return;
      }
      handleSetProductInCart(
        selectedProduct,
        drawerQuantity,
        selectedDrawerVariant ?? undefined
      );
    }
  };

  /**
   * 在 Drawer 內設定商品數量並加入／更新購物車（多規格品時傳入 variant）
   */
  const handleSetProductInCart = (
    product: PreorderCampaign['products'][0],
    quantity: number,
    variant?: {
      id: string;
      unitPriceTwd: number;
      combination: Record<string, string>;
    }
  ) => {
    if (quantity <= 0) {
      handleRemoveFromCart(product.productId, variant?.id);
      return;
    }
    if (quantity > product.remainingQuantity) {
      toast.error('超過該商品最大可預購數量');
      return;
    }
    const variantId = variant?.id ?? '';
    const priceTwd = variant?.unitPriceTwd ?? product.productPriceTwd;
    const variantLabel = variant
      ? Object.entries(variant.combination)
          .map(([k, v]) => `${k}: ${v}`)
          .join(' / ')
      : undefined;
    const existing = cart.find(
      item =>
        item.productId === product.productId &&
        (item.productVariantId ?? '') === variantId
    );
    if (existing) {
      setCart(prev =>
        prev.map(item =>
          item.productId === product.productId &&
          (item.productVariantId ?? '') === variantId
            ? { ...item, quantity }
            : item
        )
      );
    } else {
      setCart(prev => [
        ...prev,
        {
          productId: product.productId,
          productName: product.productName,
          productPriceTwd: priceTwd,
          productImageUrl: product.productImageUrl,
          quantity,
          remainingQuantity: product.remainingQuantity,
          ...(variant ? { productVariantId: variant.id, variantLabel } : {}),
        },
      ]);
    }
    setProductDrawerOpen(false);
    setSelectedByDimension(null);
    toast.success('已加入購物車');
  };

  /**
   * 更新購物車商品數量（多規格品時傳入 productVariantId）
   */
  const handleUpdateQuantity = (
    productId: string,
    newQuantity: number,
    productVariantId?: string
  ) => {
    if (newQuantity <= 0) {
      handleRemoveFromCart(productId, productVariantId);
      return;
    }
    const vid = productVariantId ?? '';
    const item = cart.find(
      i => i.productId === productId && (i.productVariantId ?? '') === vid
    );
    if (item && newQuantity > item.remainingQuantity) {
      toast.error('超過該商品最大可預購數量');
      return;
    }
    setCart(prev =>
      prev.map(i =>
        i.productId === productId && (i.productVariantId ?? '') === vid
          ? { ...i, quantity: newQuantity }
          : i
      )
    );
  };

  /**
   * 從購物車移除商品（多規格品時傳入 productVariantId 以移除該規格）
   */
  const handleRemoveFromCart = (
    productId: string,
    productVariantId?: string
  ) => {
    const vid = productVariantId ?? '';
    setCart(prev =>
      prev.filter(
        item =>
          !(
            item.productId === productId &&
            (item.productVariantId ?? '') === vid
          )
      )
    );
  };

  /**
   * 計算總金額（元；API 單價為分，使用共用 util 轉元後加總）
   */
  const totalAmount = useMemo(() => {
    return cart.reduce((sum, item) => {
      const priceYuan = centsToYuan(item.productPriceTwd);
      const qty = Number.isFinite(item.quantity) ? item.quantity : 0;
      return sum + priceYuan * qty;
    }, 0);
  }, [cart]);

  // 計算點數折抵後的金額
  const pointsDiscount = useMemo(() => {
    return pointsRedeemAmount; // 點數折抵金額（元）
  }, [pointsRedeemAmount]);

  // 計算折扣後的小計（目前預購商品沒有折扣，保留為未來擴充）
  const subtotalAfterDiscount = useMemo(() => {
    return Math.max(0, totalAmount - pointsDiscount);
  }, [totalAmount, pointsDiscount]);

  // 計算稅金（稅率由商家設定，自 checkout-config API 取得）
  // 稅金 = (商品總額 - 點數折抵) * 稅率
  const taxRateBps = checkoutConfig?.taxRateBps ?? 500;
  const taxAmount = useMemo(() => {
    // 稅金基於折扣後的小計計算
    return calculateTaxAmount(subtotalAfterDiscount, taxRateBps);
  }, [subtotalAfterDiscount, taxRateBps]);

  // 最終金額 = 折扣後小計 + 稅金
  const finalAmount = useMemo(() => {
    return subtotalAfterDiscount + taxAmount;
  }, [subtotalAfterDiscount, taxAmount]);

  const _handleChange = (field: keyof PreorderFormState, value: string) => {
    setFormState(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * 驗證取貨步驟（方式、時間／地址／門市、姓名、電話）
   */
  const _validatePickupStep = (): boolean => {
    if (formState.deliveryMethod === 'store_pickup') {
      if (!formState.pickupDate) {
        toast.error('請選擇取貨時間');
        return false;
      }
      if (!formState.customerName?.trim()) {
        toast.error('請填寫取餐人姓名');
        return false;
      }
      if (
        !formState.customerPhone?.trim() ||
        !/^09\d{8}$/.test(formState.customerPhone.trim())
      ) {
        toast.error('請填寫正確的取餐人電話（09 開頭 10 碼）');
        return false;
      }
    }
    if (formState.deliveryMethod === 'home_delivery') {
      if (!formState.shippingAddress) {
        toast.error('請填寫完整地址資訊');
        return false;
      }
      const a = formState.shippingAddress;
      if (!a.name?.trim()) {
        toast.error('請填寫收件人姓名');
        return false;
      }
      if (!a.phone?.trim() || !/^09\d{8}$/.test(a.phone.trim())) {
        toast.error('請填寫正確的收件人電話（09 開頭 10 碼）');
        return false;
      }
      if (!a.city?.trim()) {
        toast.error('請填寫縣市');
        return false;
      }
      if (!a.district?.trim()) {
        toast.error('請填寫區/鄉鎮');
        return false;
      }
      if (!a.address_line1?.trim()) {
        toast.error('請填寫街道地址');
        return false;
      }
    }
    if (formState.deliveryMethod === 'convenience_store_pickup') {
      if (!formState.convenienceStoreInfo) {
        toast.error('請填寫超商門市資訊');
        return false;
      }
      const c = formState.convenienceStoreInfo;
      if (
        !c.store_id?.trim() ||
        !c.store_name?.trim() ||
        !c.store_address?.trim()
      ) {
        toast.error('請填寫完整超商門市資訊（門市代碼、名稱、地址）');
        return false;
      }
      const name = formState.shippingAddress?.name?.trim();
      const phone = formState.shippingAddress?.phone?.trim();
      if (!name) {
        toast.error('請填寫收件人姓名');
        return false;
      }
      if (!phone || !/^09\d{8}$/.test(phone)) {
        toast.error('請填寫正確的收件人電話（09 開頭 10 碼）');
        return false;
      }
    }
    return true;
  };

  /**
   * 處理點擊送出預購按鈕
   */
  const handleCheckoutClick = () => {
    if (cart.length === 0) {
      toast.error('請至少選擇一個商品');
      return;
    }
    setFormStep(1);
    // 導向共用結帳頁面（將目前表單與購物車狀態透過 location.state 傳遞；URL 帶 tenantId 供後端識別）
    setIsFormDialogOpen(false);
    const search = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
    navigate(`/preorder/checkout${search}`, {
      state: {
        orderType: 'preorder',
        campaignId: campaign?.id,
        tenantId,
        formState,
        cart,
        pickupDateOptions,
        displayProducts,
        paymentProvider,
      },
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSubmitting) return;
    if (!campaign) {
      toast.error('目前無可預購檔期');
      return;
    }
    if (cart.length === 0) {
      toast.error('請至少選擇一個商品');
      return;
    }
    // 驗證取貨方式與必填欄位
    if (formState.deliveryMethod === 'store_pickup') {
      if (!formState.pickupDate) {
        toast.error('請選擇取貨時間');
        return;
      }
      if (!formState.customerName?.trim()) {
        toast.error('請填寫取餐人姓名');
        return;
      }
      if (
        !formState.customerPhone?.trim() ||
        !/^09\d{8}$/.test(formState.customerPhone.trim())
      ) {
        toast.error('請填寫正確的取餐人電話（09 開頭 10 碼）');
        return;
      }
    }
    if (
      formState.deliveryMethod === 'home_delivery' &&
      !formState.shippingAddress
    ) {
      toast.error('請填寫完整地址資訊');
      return;
    }
    if (
      formState.deliveryMethod === 'convenience_store_pickup' &&
      !formState.convenienceStoreInfo
    ) {
      toast.error('請選擇超商門市');
      return;
    }

    // 共用驗證
    const v = validatePreorderForm(formState, cart, displayProducts);
    if (!v.ok) {
      toast.error((v as { message?: string }).message ?? '驗證失敗');
      return;
    }
    if (invoiceEnabled) {
      const inv = validateInvoiceForm(formState);
      if (!inv.ok) {
        toast.error((inv as { message?: string }).message ?? '發票資料有誤');
        return;
      }
    }

    // 設置提交狀態
    setIsSubmitting(true);

    let apiBase: string;
    try {
      apiBase = getApiBase();
    } catch {
      setIsSubmitting(false);
      toast.error('API 位址未設定，請檢查環境變數 VITE_API_BASE_URL');
      return;
    }

    if (!profile?.userId) {
      setIsSubmitting(false);
      toast.error('需要 LINE ID 才能進行結帳，請確認已登入 LINE');
      return;
    }

    const customerName =
      formState.deliveryMethod === 'store_pickup'
        ? formState.customerName.trim()
        : (formState.shippingAddress?.name ?? '').trim();
    const customerPhone =
      formState.deliveryMethod === 'store_pickup'
        ? formState.customerPhone.trim()
        : (formState.shippingAddress?.phone ?? '').trim();
    if (!customerName || !customerPhone) {
      setIsSubmitting(false);
      toast.error('請填寫姓名與電話');
      return;
    }

    // 送出前再次檢查：多規格品必須帶 productVariantId（以 campaign.products 為準，避免 API 回傳 key 差異）
    const products = campaign?.products ?? [];
    for (const item of cart) {
      const product = products.find(
        (p: { productId: string }) =>
          String(p.productId) === String(item.productId)
      );
      const hasVariants =
        product &&
        (Boolean((product as { hasVariants?: boolean }).hasVariants) ||
          Boolean((product as { has_variants?: boolean }).has_variants));
      if (hasVariants && !item.productVariantId) {
        setIsSubmitting(false);
        const name =
          (product as { productName?: string }).productName ?? item.productName;
        toast.error(`多規格品 ${name} 必須指定規格`);
        return;
      }
    }

    const items = cart.map(item => ({
      productId: item.productId,
      quantity: item.quantity,
      ...(item.productVariantId
        ? { productVariantId: item.productVariantId }
        : {}),
    }));

    // 使用共用 hook 進行填單與啟動支付（不再保留 legacy fallback）
    try {
      const res = await startPayment({
        apiBase,
        apiHeaders: getApiHeaders(tenantId),
        campaignId: String(campaign.id),
        items,
        pickupDate: formState.pickupDate,
        deliveryMethod: formState.deliveryMethod,
        channel: 'online_shopping',
        customerName:
          formState.deliveryMethod === 'store_pickup'
            ? formState.customerName.trim()
            : (formState.shippingAddress?.name ?? '').trim(),
        customerPhone:
          formState.deliveryMethod === 'store_pickup'
            ? formState.customerPhone.trim()
            : (formState.shippingAddress?.phone ?? '').trim(),
        customerNote: formState.customerNote?.trim(),
        lineId: profile?.userId,
        invoice: invoiceEnabled
          ? {
              type: formState.invoiceType,
              taxId: formState.taxId?.trim(),
              invoiceTitle: formState.invoiceTitle?.trim(),
              carrierType: formState.carrierType,
              carrierNum: formState.carrierNum?.trim(),
              loveCode: formState.loveCode?.trim(),
              email: formState.email?.trim(),
            }
          : undefined,
        shippingAddress: formState.shippingAddress,
        convenienceStoreInfo: formState.convenienceStoreInfo,
        paymentProvider,
        ecpayChoosePayment:
          paymentProvider === 'ecpay'
            ? ecpayChoosePaymentSelected || undefined
            : undefined,
        onUserNotRegistered: () => setShowRegisterDialog(true),
      });

      if (res.type === 'form') {
        // 與主程式／Lite 綠界流程一致：建立 form、設 action = paymentUrl、append 後 submit，不 removeChild 以免導向被取消
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = res.formAction;
        form.style.display = 'none';
        for (const [k, v] of Object.entries(res.formData)) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = k;
          input.value = String(v);
          form.appendChild(input);
        }
        document.body.appendChild(form);
        form.submit();
      } else if (res.type === 'redirectUrl') {
        window.location.replace(res.url);
      } else if (res.type === 'ok') {
        const maybeOrderNumber = res.orderNumber ?? res.orderId ?? null;
        if (paymentProvider === 'cvs_cod') {
          toast.success('訂單已成立，請至門市取貨時付款');
          if (maybeOrderNumber) {
            window.location.replace(
              `/preorder/payment/return?orderNumber=${encodeURIComponent(String(maybeOrderNumber))}`
            );
          }
        } else if (maybeOrderNumber) {
          window.location.replace(
            `/preorder/payment/return?orderNumber=${encodeURIComponent(String(maybeOrderNumber))}`
          );
        } else {
          toast.success('已建立訂單，請依導引完成付款');
        }
      }

      setIsFormDialogOpen(false);
      setIsSubmitting(false);
      return;
    } catch (e) {
      console.error('[usePreorderCheckout] startPayment failed', e);
      setIsSubmitting(false);
      toast.error(e instanceof Error ? e.message : '結帳失敗');
      return;
    }
  };

  const resetForm = () => {
    setOrderSummary(null);
    setCart([]);
    if (storage) storage.removeItem('cart');
    setFormState({
      pickupDate: pickupDateOptions[0]?.value || '',
      deliveryMethod: 'store_pickup',
      shippingAddress: null,
      convenienceStoreInfo: null,
      customerName: '',
      customerPhone: '',
      customerNote: '',
      invoiceType: 'personal',
      taxId: '',
      invoiceTitle: '',
      carrierType: 'member',
      carrierNum: '',
      loveCode: '',
      email: '',
    });
    setIsFormDialogOpen(false);
  };

  if (isLoading) {
    return <PreorderPageLoading />;
  }

  if (!isLoading && campaigns && campaigns.length >= 2 && !selectedCampaign) {
    return (
      <PreorderCampaignSelect
        campaigns={campaigns}
        onSelect={setSelectedCampaign}
        normalizeImageUrl={normalizeImageUrl}
      />
    );
  }

  if (!isLoading && (!campaigns || campaigns.length === 0)) {
    const isReal404 =
      campaignError &&
      typeof campaignError === 'object' &&
      campaignError !== null &&
      'status' in campaignError &&
      (campaignError as { status: number }).status === 404;
    const isNetworkError =
      isError &&
      campaignError &&
      (() => {
        const err = campaignError as unknown;
        return (
          err instanceof TypeError ||
          (err instanceof Error &&
            (err.message.includes('fetch') ||
              err.message.includes('network') ||
              err.message.includes('Load failed') ||
              err.message.includes('Failed to fetch')))
        );
      })();

    return (
      <PreorderEmptyState
        campaignError={campaignError}
        isErrorExcluding404={isErrorExcluding404}
        isReal404={isReal404}
        isNetworkError={isNetworkError}
        getApiBase={getApiBase}
      />
    );
  }

  if (isLoading || !campaign) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white pb-20">
      <PreorderHeader
        campaign={campaign}
        showBackButton={Boolean(
          campaigns && campaigns.length >= 2 && selectedCampaign
        )}
        onBackToCampaignList={() => {
          setCart([]);
          setSelectedProduct(null);
          setProductDrawerOpen(false);
          setSelectedCampaign(null);
        }}
        pickupDateOptions={pickupDateOptions}
        selectedPickupDate={formState.pickupDate}
        onPickupDateChange={value => {
          setCart([]);
          setSelectedProduct(null);
          setProductDrawerOpen(false);
          setFormState(prev => ({ ...prev, pickupDate: value }));
        }}
        normalizeImageUrl={normalizeImageUrl}
      />

      {orderSummary ? (
        /* 訂單成功頁面 */
        <div className="px-4 py-6">
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center space-y-4 max-w-md mx-auto">
            <div className="text-green-600 text-5xl">✓</div>
            <h2 className="text-xl font-bold text-gray-900">預購成功</h2>
            <p className="text-sm text-gray-600">
              系統已同步網站通知，請保留以下資訊以利取貨。
            </p>
            <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
              <p>
                訂單編號：
                <span className="font-semibold">
                  {orderSummary.orderNumber}
                </span>
              </p>
              <p>預購數量：{orderSummary.quantity}</p>
              <p>應付金額：{formatTwdFromCents(orderSummary.totalTwd)}</p>
            </div>
            <button
              type="button"
              onClick={resetForm}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium py-3 px-4 rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              再預購一筆
            </button>
          </div>
        </div>
      ) : (
        <>
          <PreorderProductList
            displayProducts={displayProducts}
            openProductDrawer={openProductDrawer}
            productDrawerOpen={productDrawerOpen}
            onProductDrawerOpenChange={setProductDrawerOpen}
            selectedProduct={selectedProduct}
            drawerQuantity={drawerQuantity}
            selectedByDimension={selectedByDimension}
            setSelectedByDimension={setSelectedByDimension}
            selectedDrawerVariant={selectedDrawerVariant}
            onDrawerStepperMinus={handleDrawerStepperMinus}
            onDrawerStepperPlus={handleDrawerStepperPlus}
            onDrawerAddToCart={handleDrawerAddToCart}
            drawerStepperAtMin={drawerStepperAtMin}
            drawerStepperAtMax={drawerStepperAtMax}
            normalizeImageUrl={normalizeImageUrl}
          />

          <PreorderCartSummary
            cart={cart}
            totalAmount={totalAmount}
            finalAmount={finalAmount}
            subtotalAfterDiscount={subtotalAfterDiscount}
            taxAmount={taxAmount}
            taxRateBps={taxRateBps}
            pointsRedeemAmount={pointsRedeemAmount}
            onCheckout={handleCheckoutClick}
            onUpdateQuantity={handleUpdateQuantity}
            onRemoveFromCart={handleRemoveFromCart}
            formatMoney={formatMoney}
            formatTwdFromCents={formatTwdFromCents}
          />

          {/* 表單對話框：發票 → 付款（與 /preorder/checkout 共用 2 步流程） */}
          {isSharedDialogOpen && (
            <CheckoutInvoicePaymentPage
              onClose={() => {
                setIsSharedDialogOpen(false);
                setFormStep(1);
              }}
              formState={formState}
              setFormState={s => {
                if (typeof s === 'function') {
                  setFormState(prev =>
                    (s as (p: typeof prev) => typeof prev)(prev)
                  );
                } else {
                  setFormState(prev => ({ ...prev, ...(s as object) }));
                }
              }}
              formStep={formStep}
              setFormStep={setFormStep}
              cart={cart}
              isSubmitting={isSubmitting}
              paymentProvider={paymentProvider}
              setPaymentProvider={setPaymentProvider}
              onSubmit={handleSubmit}
              invoiceEnabled={invoiceEnabled}
              linePayEnabled={checkoutConfig?.linePayEnabled ?? true}
              ecpayPaymentEnabled={checkoutConfig?.ecpayPaymentEnabled ?? true}
              ecpayChoosePaymentList={checkoutConfig?.ecpayChoosePayment}
              ecpayChoosePaymentSelected={ecpayChoosePaymentSelected}
              setEcpayChoosePaymentSelected={setEcpayChoosePaymentSelected}
              showCvsCodOption={
                formState.deliveryMethod === 'convenience_store_pickup'
              }
            />
          )}
        </>
      )}

      {/* 註冊確認對話框 */}
      {showRegisterDialog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={e => {
            // 點擊背景關閉對話框
            if (e.target === e.currentTarget) {
              setShowRegisterDialog(false);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-red-100 rounded-full p-3">
                  <svg
                    className="w-8 h-8 text-red-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                尚未註冊為會員
              </h3>
              <p className="text-gray-600 text-center mb-6">
                此 LINE ID 尚未註冊為會員，請先完成會員註冊後再進行結帳
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowRegisterDialog(false)}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // 導航到會員註冊頁面，並帶上 returnTo 和 tenantId 參數
                    // 保存當前的表單狀態到 URL，以便返回時恢復
                    const currentPath = window.location.pathname;
                    const searchParams = new URLSearchParams(
                      window.location.search
                    );

                    // 如果有選擇的商品、取貨時間等，可以保存到 URL 參數中
                    // 目前先保存基本的路徑和查詢參數
                    const returnUrl =
                      currentPath +
                      (searchParams.toString()
                        ? `?${searchParams.toString()}`
                        : '');

                    // 確保帶上 tenantId 參數，以便註冊時能正確區隔租戶
                    const memberUrl = new URLSearchParams();
                    memberUrl.set('returnTo', returnUrl);
                    if (tenantId) {
                      memberUrl.set('tenantId', tenantId);
                    }

                    navigate(`/member?${memberUrl.toString()}`);
                    setShowRegisterDialog(false);
                  }}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-all"
                >
                  前往註冊
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
